/**
 * Content Replication panel — master-admin only. Exports the current site's
 * products / categories / suppliers / module configs / module instances as
 * a downloadable JSON snapshot and imports one back with a per-conflict
 * confirm step.
 *
 * Flow:
 *   1. Export → downloads `slh-snapshot-YYYY-MM-DD.json`.
 *   2. Import → pick a file → dry-run POST returns the conflicts.
 *      • If no conflicts, we apply straight away.
 *      • Otherwise show a dialog listing every conflicting record with
 *        Overwrite / Skip toggles + bulk buttons.
 *   3. Apply POSTs the snapshot with the decisions map.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, Loader2, RefreshCw, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const ENTITY_LABELS = {
  products: "Products",
  categories: "Categories",
  suppliers: "Suppliers",
  modules: "Module type configs",
  module_instances: "Module instances",
  pages: "Pages",
  navigation: "Navigation",
  site_styles: "Site styles",
  images: "Images / uploads",
};
const ENTITY_KEYS = Object.keys(ENTITY_LABELS);
// Heavy entities are excluded by default because their payloads can be big.
// Admins can still tick them from the UI.
const DEFAULT_ENTITY_KEYS = ENTITY_KEYS.filter((k) => k !== "images");
const HEAVY_ENTITIES = new Set(["images"]);

export default function ReplicationPanel() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [snapshot, setSnapshot] = useState(null);   // pending snapshot ready to apply
  const [conflicts, setConflicts] = useState(null); // dict {entity: [{id,label}]}
  const [plan, setPlan] = useState(null);           // dict {entity: {incoming, conflicts, new}}
  const [decisions, setDecisions] = useState({});   // {entity: {id: "overwrite"|"skip"}}
  const [applying, setApplying] = useState(false);
  const [inspecting, setInspecting] = useState(false);

  // Direct-push (target URL) state. `pushMode` is null when using the JSON
  // upload flow; set to "push" when the conflicts dialog was opened by a
  // direct-push dry-run so Apply calls /push again with the decisions.
  const [pushConfig, setPushConfig] = useState({
    target_url: (typeof window !== "undefined" && window.localStorage.getItem("slh.replicate.target_url")) || "",
    username: (typeof window !== "undefined" && window.localStorage.getItem("slh.replicate.username")) || "admin",
    password: "",
  });
  // Incremental push: only re-send docs the target doesn't already have.
  // Persisted so admins keep their choice across reloads. Defaults ON — a
  // fresh admin will get faster, safer image pushes right away.
  const [incremental, setIncremental] = useState(() => {
    if (typeof window === "undefined") return true;
    const raw = window.localStorage.getItem("slh.replicate.incremental");
    return raw === null ? true : raw === "true";
  });
  const toggleIncremental = () => {
    setIncremental((v) => {
      const next = !v;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("slh.replicate.incremental", String(next));
      }
      return next;
    });
  };
  // Which entities to include in export / push. Persist choice across
  // reloads so admins don't have to re-tick every time.
  const [selectedEntities, setSelectedEntities] = useState(() => {
    if (typeof window === "undefined") return new Set(DEFAULT_ENTITY_KEYS);
    const raw = window.localStorage.getItem("slh.replicate.entities");
    if (!raw) return new Set(DEFAULT_ENTITY_KEYS);
    try {
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr.filter((k) => ENTITY_KEYS.includes(k)) : DEFAULT_ENTITY_KEYS);
    } catch { return new Set(DEFAULT_ENTITY_KEYS); }
  });
  const toggleEntity = (key) => {
    setSelectedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("slh.replicate.entities", JSON.stringify([...next]));
      }
      return next;
    });
  };
  const entitiesParam = () => [...selectedEntities].join(",");
  const [pushMode, setPushMode] = useState(null);
  const [pushing, setPushing] = useState(false);
  const savePushPrefs = (patch) => {
    setPushConfig((p) => {
      const next = { ...p, ...patch };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("slh.replicate.target_url", next.target_url || "");
        window.localStorage.setItem("slh.replicate.username", next.username || "");
      }
      return next;
    });
  };

  const doExport = async () => {
    if (selectedEntities.size === 0) { toast.error("Pick at least one entity"); return; }
    setExporting(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/replicate/export?entities=${encodeURIComponent(entitiesParam())}`,
        { withCredentials: true },
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      link.href = url;
      link.download = `slh-snapshot-${date}.json`;
      link.click();
      URL.revokeObjectURL(url);
      const totals = Object.entries(data.counts || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Snapshot downloaded (${totals || "empty"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const doPickFile = () => fileRef.current?.click();

  const doInspect = async (file) => {
    if (!file) return;
    setInspecting(true);
    try {
      const text = await file.text();
      const snap = JSON.parse(text);
      const { data } = await axios.post(
        `${API}/admin/replicate/import`,
        { snapshot: snap, dry_run: true },
        { withCredentials: true },
      );
      const conf = data?.conflicts || {};
      const hasConf = Object.values(conf).some((rows) => rows.length > 0);
      if (!hasConf) {
        // Nothing conflicts — apply straight away.
        await applyImport(snap, {}, "overwrite");
      } else {
        // Default every conflict to "skip" so the admin has to opt in to overwrite.
        const init = {};
        for (const [ent, rows] of Object.entries(conf)) {
          init[ent] = Object.fromEntries(rows.map((r) => [r.id, "skip"]));
        }
        setSnapshot(snap);
        setConflicts(conf);
        setPlan(data?.plan || {});
        setDecisions(init);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import inspection failed");
    } finally {
      setInspecting(false);
      if (fileRef.current) fileRef.current.value = ""; // allow same file again
    }
  };

  const applyImport = async (snap, decisionMap, defaultAction) => {
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/replicate/import`,
        {
          snapshot: snap,
          decisions: decisionMap,
          default_action: defaultAction,
        },
        { withCredentials: true },
      );
      const totals = Object.entries(data.applied || {})
        .map(([k, v]) => `${k}: +${v.inserted} new, ${v.overwritten} overwritten, ${v.skipped} skipped`)
        .join(" · ");
      toast.success(`Snapshot applied. ${totals || ""}`);
      setSnapshot(null);
      setConflicts(null);
      setPlan(null);
      setDecisions({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setApplying(false);
    }
  };

  const setAll = (entity, action) => {
    setDecisions((prev) => {
      const next = { ...prev };
      const rows = conflicts?.[entity] || [];
      next[entity] = Object.fromEntries(rows.map((r) => [r.id, action]));
      return next;
    });
  };

  const setOne = (entity, id, action) => {
    setDecisions((prev) => ({
      ...prev,
      [entity]: { ...(prev[entity] || {}), [id]: action },
    }));
  };

  const submitDecisions = () => {
    if (pushMode === "push") {
      return applyPush(decisions, "skip");
    }
    return applyImport(snapshot, decisions, "skip");
  };

  const doPushDryRun = async () => {
    if (!pushConfig.target_url) { toast.error("Target URL is required"); return; }
    if (!pushConfig.password) { toast.error("Admin password is required"); return; }
    if (selectedEntities.size === 0) { toast.error("Pick at least one entity"); return; }
    setPushing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/replicate/push`,
        { ...pushConfig, entities: entitiesParam(), dry_run: true, incremental },
        { withCredentials: true },
      );
      const conf = data?.conflicts || {};
      const hasConf = Object.values(conf).some((rows) => rows.length > 0);
      if (!hasConf) {
        await applyPush({}, "overwrite");
      } else {
        const init = {};
        for (const [ent, rows] of Object.entries(conf)) {
          init[ent] = Object.fromEntries(rows.map((r) => [r.id, "skip"]));
        }
        setConflicts(conf);
        setPlan(data?.plan || {});
        setDecisions(init);
        setPushMode("push");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push dry-run failed");
    } finally {
      setPushing(false);
    }
  };

  // Live progress state — populated by the streaming push endpoint. `null`
  // when idle; the progress card renders whenever this is non-null.
  const [progress, setProgress] = useState(null);

  const applyPush = async (decisionMap, defaultAction) => {
    setApplying(true);
    setProgress({ index: 0, total: 0, label: "Preparing…", chunks: [], skipped_by_manifest: {} });
    try {
      const resp = await fetch(`${API}/admin/replicate/push-stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...pushConfig,
          entities: entitiesParam(),
          dry_run: false,
          decisions: decisionMap,
          default_action: defaultAction,
          incremental,
        }),
      });
      if (!resp.ok || !resp.body) {
        // Fall back to the JSON error body when the stream never opens.
        let detail = `HTTP ${resp.status}`;
        try { const j = await resp.json(); detail = j.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let final = null;
      let hadError = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf("\n")) !== -1) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          if (evt.event === "plan") {
            setProgress((p) => ({ ...p, total: evt.total_chunks, skipped_by_manifest: evt.skipped_by_manifest || {} }));
          } else if (evt.event === "chunk_start") {
            setProgress((p) => ({ ...p, index: evt.index, total: evt.total, label: evt.label }));
          } else if (evt.event === "chunk_ok") {
            setProgress((p) => ({ ...p, index: evt.index, label: `${evt.label} ✓` }));
          } else if (evt.event === "chunk_error") {
            hadError = evt;
            setProgress((p) => ({ ...p, label: `${evt.label} — ${evt.error?.slice(0, 200) || "failed"}` }));
          } else if (evt.event === "done") {
            final = evt;
          } else if (evt.event === "error") {
            hadError = evt;
          }
        }
      }
      if (hadError) {
        throw new Error(hadError.error || hadError.message || "Push failed");
      }
      if (final) {
        const totals = Object.entries(final.applied || {})
          .map(([k, v]) => `${k}: +${v.inserted} new, ${v.overwritten} overwritten, ${v.skipped} skipped`)
          .join(" · ");
        const preSkipped = Object.entries(final.skipped_by_manifest || {})
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${v} ${k} already on target`)
          .join(", ");
        toast.success(
          `Pushed to ${final.target}. ${totals || ""}${preSkipped ? ` (${preSkipped})` : ""}`,
        );
      }
      setConflicts(null);
      setPlan(null);
      setDecisions({});
      setPushMode(null);
    } catch (e) {
      toast.error(e.message || "Push failed");
    } finally {
      setApplying(false);
      // Keep the last progress row visible for a moment so the admin can
      // see WHICH chunk finished/failed, then fade it out.
      setTimeout(() => setProgress(null), 4000);
    }
  };

  return (
    <div className="space-y-4" data-testid="replication-panel">
      {/* Shared entity picker — controls both export and push */}
      <div className="rounded-md border border-border bg-muted/20 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-lg font-semibold">What to include</h3>
          <div className="flex gap-2">
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => {
                const all = new Set(ENTITY_KEYS);
                setSelectedEntities(all);
                if (typeof window !== "undefined") window.localStorage.setItem("slh.replicate.entities", JSON.stringify([...all]));
              }}
              data-testid="replicate-entities-all"
            >
              Select all
            </button>
            <span className="text-xs text-muted-foreground">·</span>
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => {
                setSelectedEntities(new Set());
                if (typeof window !== "undefined") window.localStorage.setItem("slh.replicate.entities", JSON.stringify([]));
              }}
              data-testid="replicate-entities-none"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {ENTITY_KEYS.map((k) => (
            <label
              key={k}
              className={`flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded border transition-colors ${
                selectedEntities.has(k) ? "bg-primary/5 border-primary/40 text-foreground" : "bg-background border-border text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`replicate-entity-${k}`}
              title={HEAVY_ENTITIES.has(k) ? "Large payload — can be hundreds of MB. Push may be slow." : undefined}
            >
              <input
                type="checkbox"
                checked={selectedEntities.has(k)}
                onChange={() => toggleEntity(k)}
                className="h-3.5 w-3.5"
                data-testid={`replicate-entity-${k}-checkbox`}
              />
              <span className="truncate">{ENTITY_LABELS[k]}</span>
              {HEAVY_ENTITIES.has(k) && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
                  heavy
                </span>
              )}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Applies to both export and push. Tick only what you need — e.g. push just products or just pages.
        </p>
      </div>

      <div className="rounded-md border border-border bg-muted/20 p-4">
        <h3 className="text-lg font-semibold mb-1">Replicate to production</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Download a JSON snapshot of your CMS content (products, categories, suppliers, module type configs, module instances)
          and re-import it on your production admin. Conflicts (records with the same id) are surfaced for per-record confirmation.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={doExport}
            disabled={exporting}
            data-testid="replicate-export-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export snapshot
          </Button>
          <Button
            onClick={doPickFile}
            disabled={inspecting}
            data-testid="replicate-import-btn"
          >
            {inspecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Import snapshot
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => doInspect(e.target.files?.[0])}
            data-testid="replicate-import-file"
          />
        </div>
      </div>

      {/* Direct push to another environment */}
      <div className="rounded-md border border-border bg-muted/20 p-4">
        <h3 className="text-lg font-semibold mb-1">Push directly to production</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Skip the download/upload and push straight into another Sell Local Homes deployment.
          Enter the target site&apos;s URL and its master admin credentials. The URL + username are remembered locally,
          the password is never stored.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-xs">Target URL</Label>
            <Input
              type="url"
              placeholder="https://your-site.com"
              value={pushConfig.target_url}
              onChange={(e) => savePushPrefs({ target_url: e.target.value })}
              data-testid="replicate-push-url"
            />
          </div>
          <div>
            <Label className="text-xs">Admin username</Label>
            <Input
              type="text"
              value={pushConfig.username}
              onChange={(e) => savePushPrefs({ username: e.target.value })}
              data-testid="replicate-push-username"
            />
          </div>
          <div>
            <Label className="text-xs">Admin password</Label>
            <Input
              type="password"
              value={pushConfig.password}
              onChange={(e) => setPushConfig((p) => ({ ...p, password: e.target.value }))}
              data-testid="replicate-push-password"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={doPushDryRun}
            disabled={pushing || !pushConfig.target_url || !pushConfig.password}
            data-testid="replicate-push-btn"
          >
            {pushing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
            Push to production
          </Button>
          <label
            className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none"
            data-testid="replicate-incremental-label"
            title="Ask the target for its manifest first and only send files it doesn't already have. Recommended — turns a re-push into a delta push."
          >
            <input
              type="checkbox"
              checked={incremental}
              onChange={toggleIncremental}
              className="h-3.5 w-3.5"
              data-testid="replicate-incremental-checkbox"
            />
            <span>Incremental — only send files missing from target</span>
          </label>
        </div>
        {progress && (
          <div className="mt-4 rounded-md border border-border bg-background p-3 space-y-2" data-testid="replicate-progress">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground truncate" data-testid="replicate-progress-label">
                {progress.label || "Preparing…"}
              </span>
              <span className="text-muted-foreground tabular-nums shrink-0 ml-2" data-testid="replicate-progress-counter">
                {progress.index} / {progress.total || "?"}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200"
                style={{
                  width: progress.total ? `${Math.min(100, Math.round((progress.index / progress.total) * 100))}%` : "10%",
                }}
                data-testid="replicate-progress-bar"
              />
            </div>
            {Object.entries(progress.skipped_by_manifest || {}).filter(([, v]) => v > 0).length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                Skipped by manifest:{" "}
                {Object.entries(progress.skipped_by_manifest)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => `${v} ${k}`)
                  .join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!conflicts} onOpenChange={(o) => { if (!o) { setConflicts(null); setSnapshot(null); setPushMode(null); } }}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden" data-testid="replicate-conflict-dialog">
          <DialogHeader>
            <DialogTitle>Resolve import conflicts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              The records below already exist. Choose <strong>Overwrite</strong> to replace the target with
              the snapshot&apos;s version, or <strong>Skip</strong> to keep the target&apos;s version. New records (not shown)
              are always imported.
            </p>
            {Object.entries(conflicts || {}).map(([entity, rows]) => {
              if (!rows || rows.length === 0) return null;
              const p = plan?.[entity] || {};
              return (
                <div key={entity} className="border border-border rounded-md" data-testid={`replicate-conflicts-${entity}`}>
                  <div className="px-3 py-2 bg-muted/40 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-semibold">{ENTITY_LABELS[entity] || entity}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {p.new ?? 0} new · {rows.length} conflicts · {p.incoming ?? 0} total
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setAll(entity, "overwrite")} data-testid={`replicate-all-overwrite-${entity}`}>
                        Overwrite all
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAll(entity, "skip")} data-testid={`replicate-all-skip-${entity}`}>
                        Skip all
                      </Button>
                    </div>
                  </div>
                  <ul className="divide-y divide-border max-h-64 overflow-y-auto">
                    {rows.map((r) => {
                      const action = decisions?.[entity]?.[r.id] || "skip";
                      return (
                        <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`replicate-conflict-row-${entity}-${r.id}`}>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{r.label}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{r.id}</div>
                          </div>
                          <div className="inline-flex rounded overflow-hidden border border-border shrink-0 text-xs">
                            <button
                              type="button"
                              onClick={() => setOne(entity, r.id, "overwrite")}
                              className={`px-2 py-1 ${action === "overwrite" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                              data-testid={`replicate-overwrite-${entity}-${r.id}`}
                            >
                              Overwrite
                            </button>
                            <button
                              type="button"
                              onClick={() => setOne(entity, r.id, "skip")}
                              className={`px-2 py-1 ${action === "skip" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                              data-testid={`replicate-skip-${entity}-${r.id}`}
                            >
                              Skip
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConflicts(null); setSnapshot(null); setPushMode(null); }} data-testid="replicate-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitDecisions} disabled={applying} data-testid="replicate-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
